import { z } from "zod";

/**
 * Why the last sync of an MCP server failed — the vocabulary of
 * `McpServer.lastSyncErrorCode`.
 *
 * The column is a string, not a Prisma enum, so a new failure class is added here
 * and needs no migration. The database bounds the column to an upper-snake token
 * and nothing more, so a reader parses it with {@link McpSyncErrorCodeSchema} and
 * handles a code it does not know — a pod running an older release reads codes a
 * newer one wrote.
 *
 * A code names a CLASS of failure and never carries remote text: an error body is
 * server-authored, untrusted, and can echo a credential.
 */
export const MCP_SYNC_ERROR_CODES = [
  /** DNS or TCP failed: nothing answered. */
  "UNREACHABLE",
  /** The egress guard refused the resolved address or a redirect hop. */
  "EGRESS_REFUSED",
  /** The server did not answer within the client's deadline. */
  "TIMEOUT",
  /** The TLS handshake failed or the certificate did not verify. */
  "TLS_FAILURE",
  /** An HTTP status that is neither success nor an authorization answer. */
  "HTTP_ERROR",
  /** The server requires authorization and no usable credential exists. */
  "AUTHORIZATION_REQUIRED",
  /** The server refused the credential that was presented. */
  "AUTHORIZATION_REJECTED",
  /** No MCP protocol version both sides support. */
  "PROTOCOL_VERSION_UNSUPPORTED",
  /** A malformed JSON-RPC or MCP message. */
  "PROTOCOL_ERROR",
  /** A response larger than the client accepts. */
  "RESPONSE_TOO_LARGE",
  /** A `tools/list` result that failed validation. */
  "INVALID_TOOL_LIST",
  /**
   * The discovery was never ASKED FOR: the queue refused the job, so nothing will
   * dial this server until somebody syncs it by hand.
   *
   * 🔴 THE ONE CODE IN THIS LIST THAT IS NOT ABOUT THE REMOTE. Every other member
   * is a verdict on a server we reached or tried to reach; this one is a verdict on
   * US, recorded without a single packet leaving the process. It is here rather
   * than in a vocabulary of its own because the column it lands in answers one
   * question — *why has this server no tools* — and an operator reading that
   * question needs our own failures in the same answer as the remote's.
   *
   * It is written beside `FAILED` and never beside `QUEUED`, and that is forced
   * rather than chosen: `McpServer_sync_error_code_iff_failed` is a biconditional,
   * so an error code is representable ONLY alongside a FAILED outcome. There is no
   * queued-failed state to invent, and nothing was queued, so FAILED is also the
   * honest reading — the attempt failed at the earliest stage there is.
   */
  "DISCOVERY_NOT_QUEUED"
] as const;

export const McpSyncErrorCodeSchema = z.enum(MCP_SYNC_ERROR_CODES);

export type McpSyncErrorCode = z.infer<typeof McpSyncErrorCodeSchema>;
